import { useState } from 'react';

import {
  ContainerEngine,
  Environment,
} from '@/react/portainer/environments/types';
import {
  commandsTabs,
  CommandTab,
} from '@/react/edge/components/EdgeScriptForm/scripts';
import { Platform } from '@/react/edge/components/EdgeScriptForm/types';
import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import EdgeAgentStandardIcon from '@/react/edge/components/edge-agent-standard.svg?c';
import EdgeAgentAsyncIcon from '@/react/edge/components/edge-agent-async.svg?c';

import { BoxSelector } from '@@/BoxSelector';
import { Loading } from '@@/Widget';
import { BoxSelectorOption } from '@@/BoxSelector/types';
import { FormControl } from '@@/form-components/FormControl';
import { ButtonSelector } from '@@/form-components/ButtonSelector/ButtonSelector';

import { AnalyticsStateKey } from '../types';
import { EdgeAgentTab } from '../shared/EdgeAgentTab';
import { SSHInstall } from '../WizardKubesoloBulk/SSHInstall';
import { useActiveConnectionTest } from '../WizardKubesoloBulk/queries/useActiveConnectionTest';

interface Props {
  /** Takes the new environment's ID only, since an install returns no environment */
  onCreate(
    environment: Pick<Environment, 'Id'>,
    analytics: AnalyticsStateKey
  ): void;
}

type CreationType = 'edgeAgentStandard' | 'edgeAgentAsync';

type Installation = 'singleNode' | 'manyMachines';

const creationTypeOptions: BoxSelectorOption<CreationType>[] = [
  {
    id: 'edgeAgentStandard',
    icon: EdgeAgentStandardIcon,
    iconType: 'badge',
    label: 'Edge Agent Standard',
    description:
      'The remote environment will initiate connections to the Portainer server, with the ability to open a secure on-demand tunnel for real-time interaction. The Portainer server must be accessible from the Edge Agent environment.',
    value: 'edgeAgentStandard',
  },
  ...(isBE
    ? [
        {
          id: 'edgeAgentAsync',
          icon: EdgeAgentAsyncIcon,
          iconType: 'badge' as const,
          label: 'Edge Agent Async',
          description:
            'The remote environment will initiate connections to the Portainer server, without the ability to open a real-time tunnel. The Portainer server must be accessible from the Edge Agent environment.',
          value: 'edgeAgentAsync' as const,
        },
      ]
    : []),
];

const kubesoloCommands: CommandTab[] = [
  commandsTabs.kubesoloLinux,
  {
    ...commandsTabs.k8sLinux,
    id: 'standalone' as Platform,
    label: 'Manual',
  },
];

export function WizardKubesolo({ onCreate }: Props) {
  const activeTestQuery = useActiveConnectionTest();

  if (activeTestQuery.isLoading) {
    return <Loading />;
  }

  const activeTest = activeTestQuery.data;

  return (
    <KubesoloOptions
      onCreate={onCreate}
      initialCreationType={
        activeTest?.environmentSettings.asyncMode
          ? 'edgeAgentAsync'
          : 'edgeAgentStandard'
      }
      // reopening the wizard during a test goes back to it
      initialInstallation={activeTest ? 'manyMachines' : 'singleNode'}
    />
  );
}

/** Starts from the test Portainer holds, and keeps its choices once that test is cleared. */
function KubesoloOptions({
  onCreate,
  initialCreationType,
  initialInstallation,
}: Props & {
  initialCreationType: CreationType;
  initialInstallation: Installation;
}) {
  const [creationType, setCreationType] = useState(initialCreationType);
  const [installation, setInstallation] = useState(initialInstallation);

  return (
    <div className="form-horizontal">
      <BoxSelector
        onChange={(v) => setCreationType(v)}
        options={creationTypeOptions}
        value={creationType}
        radioName="creation-type"
      />

      <FormControl label="KubeSolo installation">
        <ButtonSelector<Installation>
          options={[
            { value: 'singleNode', label: 'Deploy a single node' },
            {
              value: 'manyMachines',
              label: 'Install KubeSolo on many machines',
            },
          ]}
          value={installation}
          onChange={setInstallation}
          className="[&>label]:!h-auto [&>label]:!whitespace-normal"
        />
      </FormControl>

      {installation === 'manyMachines' && (
        <SSHInstall
          asyncMode={creationType === 'edgeAgentAsync'}
          onInstalled={(environmentIds) =>
            environmentIds.forEach((Id) =>
              onCreate(
                { Id },
                creationType === 'edgeAgentAsync'
                  ? 'kubesoloEdgeAgentAsync'
                  : 'kubesoloEdgeAgentStandard'
              )
            )
          }
        />
      )}

      {installation === 'singleNode' &&
        creationType === 'edgeAgentStandard' && (
          <EdgeAgentTab
            onCreate={(environment) =>
              onCreate(environment, 'kubesoloEdgeAgentStandard')
            }
            commands={kubesoloCommands}
            containerEngine={ContainerEngine.Kubernetes}
          />
        )}

      {installation === 'singleNode' && creationType === 'edgeAgentAsync' && (
        <EdgeAgentTab
          asyncMode
          onCreate={(environment) =>
            onCreate(environment, 'kubesoloEdgeAgentAsync')
          }
          commands={kubesoloCommands}
          containerEngine={ContainerEngine.Kubernetes}
        />
      )}
    </div>
  );
}
